import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [mobileSocialsOpen, setMobileSocialsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [consentToContact, setConsentToContact] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consentToContact) {
      setSubmitError('Please agree to the contact consent before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        });

      if (error) {
        throw error;
      }

      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setConsentToContact(false);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black' : 'bg-black/90'
        }`}
      >
        <div className="flex items-center justify-between h-20 md:h-24 px-4 md:px-8">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1d9414342dd58a00fdde5ee22dbe4d3e.png"
              alt="Rides & Dives Logo"
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white text-2xl cursor-pointer"
          >
            <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            <Link
              to="/rides"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Rides
            </Link>
            <Link
              to="/dives"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Dives
            </Link>
            <Link
              to="/gear"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Gear
            </Link>
            <Link
              to="/gallery"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Gallery
            </Link>
            <Link
              to="/merch"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Merch
            </Link>
            <Link
              to="/trip-planner"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              Plan
            </Link>
            <div 
              className="relative"
              onMouseEnter={() => setSocialsOpen(true)}
              onMouseLeave={() => setSocialsOpen(false)}
            >
              <button
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                Follow
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    socialsOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {socialsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-black border border-white/10 py-2 min-w-[160px]">
                    <a
                      href="https://www.youtube.com/@RidesDives"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a
                      href="https://www.instagram.com/rides.and.dives/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a
                      href="https://www.tiktok.com/@ridesanddives"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a
                      href="https://www.facebook.com/share/g/1AqygJbGTi/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about"
              className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-sm font-light tracking-widest uppercase text-[#8B6F47] cursor-pointer whitespace-nowrap"
            >
              Contact
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black border-t border-white/10">
            <div className="flex flex-col py-4 px-4 space-y-4">
              <Link
                to="/rides"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Rides
              </Link>
              <Link
                to="/dives"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Dives
              </Link>
              <Link
                to="/gear"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Gear
              </Link>
              <Link
                to="/gallery"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Gallery
              </Link>
              <Link
                to="/merch"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Merch
              </Link>
              <Link
                to="/trip-planner"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                Plan
              </Link>

              {/* Mobile Socials accordion */}
              <div>
                <button
                  onClick={() => setMobileSocialsOpen(!mobileSocialsOpen)}
                  className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2 flex items-center gap-1 w-full"
                >
                  Follow
                  <i
                    className={`ri-arrow-down-s-line transition-transform ${
                      mobileSocialsOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {mobileSocialsOpen && (
                  <div className="pl-4 space-y-3 mt-2">
                    <a
                      href="https://www.youtube.com/@RidesDives"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1"
                    >
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a
                      href="https://www.instagram.com/rides.and.dives/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1"
                    >
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a
                      href="https://www.tiktok.com/@ridesanddives"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1"
                    >
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a
                      href="https://www.facebook.com/share/g/1AqygJbGTi/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1"
                    >
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                )}
              </div>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-light tracking-widest uppercase text-[#8B6F47] cursor-pointer py-2"
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Get In Touch</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Have a question, suggestion, or just want to say hello? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 md:p-10">
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#8B6F47]/20 rounded-full">
                  <i className="ri-check-line text-3xl text-[#8B6F47]"></i>
                </div>
                <h3 className="text-2xl font-semibold mb-2">Message Sent!</h3>
                <p className="text-white/70 mb-6">Thank you for reaching out. We'll get back to you soon.</p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="text-[#8B6F47] hover:underline cursor-pointer"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Your Name <span className="text-[#8B6F47]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Doe"
                      className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#8B6F47] transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email Address <span className="text-[#8B6F47]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                      className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#8B6F47] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-2">
                    Subject <span className="text-[#8B6F47]">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#8B6F47] transition-colors cursor-pointer"
                  >
                    <option value="" className="bg-black">Select a subject</option>
                    <option value="General Inquiry" className="bg-black">General Inquiry</option>
                    <option value="Ride Suggestion" className="bg-black">Ride Suggestion</option>
                    <option value="Dive Recommendation" className="bg-black">Dive Recommendation</option>
                    <option value="Collaboration" className="bg-black">Collaboration</option>
                    <option value="Feedback" className="bg-black">Feedback</option>
                    <option value="Other" className="bg-black">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Message <span className="text-[#8B6F47]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Tell us what's on your mind..."
                    className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#8B6F47] transition-colors resize-none"
                  ></textarea>
                  <p className="text-white/40 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {/* Consent to Contact Checkbox */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setConsentToContact(!consentToContact)}
                      className={`w-6 h-6 flex-shrink-0 rounded border-2 flex items-center justify-center cursor-pointer transition-colors mt-0.5 ${
                        consentToContact ? 'bg-[#8B6F47] border-[#8B6F47]' : 'border-white/30 hover:border-white/50'
                      }`}
                    >
                      {consentToContact && <i className="ri-check-line text-black text-sm"></i>}
                    </button>
                    <label 
                      className="text-sm text-white/70 cursor-pointer leading-relaxed"
                      onClick={() => setConsentToContact(!consentToContact)}
                    >
                      I consent to being contacted by Rides &amp; Dives via text messages and phone calls at the number or email provided. Message &amp; data rates may apply. I understand I can opt out at any time by replying STOP or contacting us directly. <span className="text-[#8B6F47]">*</span>
                    </label>
                  </div>
                </div>

                {submitError && (
                  <div className="text-red-400 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8B6F47] hover:bg-[#7A5F3A] text-white font-medium py-3 px-6 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Additional Contact Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-white/5 rounded-full">
                <i className="ri-phone-line text-xl text-[#8B6F47]"></i>
              </div>
              <h4 className="font-medium mb-1">Phone</h4>
              <a
                href="tel:+18557657326"
                className="text-white/60 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer"
              >
                (855) 765-7326
              </a>
            </div>
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-white/5 rounded-full">
                <i className="ri-mail-line text-xl text-[#8B6F47]"></i>
              </div>
              <h4 className="font-medium mb-1">Email</h4>
              <p className="text-white/60 text-sm">hello@ridesanddives.com</p>
            </div>
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-white/5 rounded-full">
                <i className="ri-instagram-line text-xl text-[#8B6F47]"></i>
              </div>
              <h4 className="font-medium mb-1">Instagram</h4>
              <a
                href="https://www.instagram.com/rides.and.dives/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer"
              >
                @rides.and.dives
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">© 2026 Rides & Dives. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link
              to="/partners"
              className="text-stone-400 text-sm hover:text-white transition-colors cursor-pointer"
            >
              Become a Partner
            </Link>
            <Link
              to="/privacy"
              className="text-stone-400 text-sm hover:text-white transition-colors cursor-pointer"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
